import { useRef, useEffect, useState } from 'react';
import MainFormContainer from '@/wrappers/MainFormContainer';
import PostcodeElement, { type PostcodeElementRef } from '@/elements/Postcode';
import Modal from '@/ui/Modal';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { updateBasicInfo, loadFromStorage } from '@/utils/client-storage/localStorage';
import { GET_JOURNEY_PAGES } from '@/routes/journey.routes/index';
import { useClaimUpsert } from '@/services/useClaimUpsert';
import { generateSession } from '@/services/useGenerateSession';
import { getUtmTagsFromUrl } from '@/utils/utm';
// import imgSignature from '@/assets/img/Signature.svg';
import { LcpDisclaimer } from '@/components/LcpDisclaimer';
import TrustpilotCarousel from '@/components/TrustpilotCarousel';
import clsx from 'clsx';
import { ArrowLeft, Info, ShieldCheck } from 'lucide-react';
import { clearAllSessionData, triggerSub2DataLayer } from '@/utils/functions';
import { SUB2_EVENTS } from '@/services/constants';
import { getActiveVariantConfig } from './variants';
import { isSocialDomain } from '@/utils/helpers';

export default function Postcode({
  homepageMode = false,
  hideAfterForm = false,
}: {
  homepageMode?: boolean;
  hideAfterForm?: boolean;
}) {
  const postcodeRef = useRef<PostcodeElementRef | null>(null);
  const router = useRouter();
  const routerState = useRouterState();
  const searchParams = (routerState.location.search || {}) as Record<string, string>;
  const lpParam = isSocialDomain ? searchParams.lp || searchParams.variant || '' : '';
  const fromParam = searchParams.from || '';
  const utmSource = searchParams.utm_source || '';
  const cleanPathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isDM = utmSource && utmSource.includes('DM');

  const isMailDomain =
    typeof window !== 'undefined' && (window.location.hostname === 'localhost' || isDM);

  const initialSocialAnswer =
    typeof window !== 'undefined' ? (window as any).__PRE_REACT_SOCIAL_ANSWER || null : null;
  const [socialAnswer, setSocialAnswer] = useState<'Yes' | 'No' | null>(initialSocialAnswer);
  const [hasProceeded, setHasProceeded] = useState(initialSocialAnswer === 'Yes');
  const [hasInteracted, setHasInteracted] = useState(!!initialSocialAnswer);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasSeenDialog, setHasSeenDialog] = useState(false);

  const activeConfig = isSocialDomain
    ? getActiveVariantConfig(lpParam || cleanPathname)
    : getActiveVariantConfig('/');

  const showSocialLayout = isSocialDomain || lpParam;

  const questionTextToSave = activeConfig.questionText;

  const { mutate: updateClaim } = useClaimUpsert();
  const effectRan = useRef(false);

  useEffect(() => {
    if (effectRan.current) return;
    effectRan.current = true;

    clearAllSessionData();
    triggerSub2DataLayer(SUB2_EVENTS.viewContent, {});

    if (initialSocialAnswer === 'Yes') {
      const answerObj = {
        question: questionTextToSave,
        answer: 'Yes',
      };
      updateBasicInfo({ landingQuestion: answerObj });

      if (isSocialDomain) {
        const saveYesClaim = async () => {
          try {
            let claimNumber = loadFromStorage('currentId');
            if (!claimNumber) {
              const res = await generateSession({
                utmTags: JSON.stringify(getUtmTagsFromUrl()),
                allowNewClaim: true,
              });
              claimNumber = res.claimId;
            }
            updateClaim({
              claimNumber: claimNumber,
              landingQuestion: answerObj,
            });
          } catch (error) {
            console.error('Failed to generate session for Yes answer:', error);
          }
        };
        saveYesClaim();
      }
    }
  }, [initialSocialAnswer, isSocialDomain, updateClaim]);

  const handleSubmit = async () => {
    const pcData = postcodeRef.current?.getData();
    const { postcode, selectedPostcode } = pcData ?? {
      postcode: '',
      selectedPostcode: '',
      addresses: [],
    };

    const finalPostcode = selectedPostcode || postcode;

    if (!finalPostcode || !postcodeRef.current?.validate()) {
      return;
    }

    if (hasSeenDialog) {
      handleContinue();
      return;
    }

    setHasSeenDialog(true);
    setIsModalOpen(true);
  };

  const handleContinue = async () => {
    setIsModalOpen(false);

    const pcData = postcodeRef.current?.getData();
    const { postcode, selectedPostcode, addresses } = pcData ?? {
      postcode: '',
      selectedPostcode: '',
      addresses: [],
    };

    const finalPostcode = selectedPostcode || postcode;

    if (!finalPostcode) {
      return;
    }

    updateBasicInfo({
      postcode: finalPostcode,
      landingQuestion: socialAnswer
        ? {
            question: questionTextToSave,
            answer: socialAnswer,
          }
        : undefined,
    });

    const destination = 'ADDRESS';
    const pages = GET_JOURNEY_PAGES() as any;

    if (pages && pages[destination]) {
      router.navigate({
        to: pages[destination].path,
        state: {
          prefetchedAddresses: addresses,
          postcode: finalPostcode,
        } as any,
      });
    } else {
      console.error('Address lookup page not found');
    }
  };

  const postCodeElementComp = (
    <form
      id="postcode-section"
      onSubmit={(e) => e.preventDefault()}
      className={clsx('w-full', !homepageMode && 'px-5')}
    >
      <PostcodeElement
        ref={postcodeRef}
        homepageMode={homepageMode}
        triggerQueryParams={true}
        title="Please enter your Current Address"
        onSubmit={handleSubmit}
      />
    </form>
  );

  const compIternal = (
    <>
      {homepageMode && (isMailDomain || isSocialDomain) && (
        <div className="inline-flex items-center gap-2 bg-white border border-primary/30 rounded-full px-4 py-2 self-start">
          <span className="w-2 h-2 rounded-full bg-primary" />
          <span className="text-primary font-semibold text-body lg:text-body">
            {isSocialDomain ? activeConfig.pillText : <>Thanks for scanning &mdash; welcome.</>}
          </span>
        </div>
      )}

      {homepageMode && showSocialLayout && !hasProceeded && (
        <div className="w-full flex flex-col items-start gap-4">
          {socialAnswer === 'No' ? (
            <>
              <h2 className="text-title lg:text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
                You are not <span className="text-primary">eligible</span>.
              </h2>
              <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
                You need to have financed more than one vehicle since 2007.
              </div>
              <div className="flex items-start gap-3 bg-white border border-primary/10 rounded-xl p-4 mt-2 mb-1">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <p className="text-body text-text-main font-medium leading-tight">
                  If you think you might have had car finance since 2007, you can click go back and
                  update your selection.
                </p>
              </div>
              <div className="w-full mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSocialAnswer(null);
                    if (fromParam) {
                      router.navigate({
                        to: '/',
                        search: {
                          lp: fromParam,
                        } as any,
                      });
                    }
                  }}
                  className="pulsating-btn relative w-full px-6 py-4 rounded-xl text-center transition-all flex items-center justify-center bg-primary text-white hover:bg-[#14532d] font-bold text-body lg:text-title shadow-sm gap-2"
                >
                  <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={3} />
                  Go Back
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-title lg:text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
                {activeConfig.headline}
              </h2>
              <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
                {activeConfig.subhead}
              </div>
              <div
                className={clsx(
                  'w-full flex gap-3 mt-2',
                  ['/black-horse', '/12-million'].includes(activeConfig.slug)
                    ? 'flex-col'
                    : 'flex-row'
                )}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSocialAnswer('Yes');
                    triggerSub2DataLayer(SUB2_EVENTS.car_financed_0721);
                    setHasProceeded(true);
                    setHasInteracted(true);

                    const answerObj = {
                      question: questionTextToSave,
                      answer: 'Yes',
                    };
                    updateBasicInfo({ landingQuestion: answerObj });

                    if (isSocialDomain) {
                      const saveYesClaim = async () => {
                        try {
                          let claimNumber = loadFromStorage('currentId');
                          if (!claimNumber) {
                            const res = await generateSession({
                              utmTags: JSON.stringify(getUtmTagsFromUrl()),
                              allowNewClaim: true,
                            });
                            claimNumber = res.claimId;
                          }
                          updateClaim({
                            claimNumber: claimNumber,
                            landingQuestion: answerObj,
                          });
                        } catch (error) {
                          console.error('Failed to generate session for Yes answer:', error);
                        }
                      };
                      saveYesClaim();
                    }

                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="pulsating-btn w-full py-4 px-6 rounded-xl text-center transition-all flex items-center justify-center bg-primary text-white hover:bg-[#14532d] font-bold text-body lg:text-title shadow-sm"
                >
                  {lpParam === '12-million' ? 'Yes — check my eligibility' : 'Yes'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (activeConfig.noButtonCustomRoute) {
                      if (lpParam) {
                        router.navigate({
                          to: '/',
                          search: {
                            lp: activeConfig.noButtonCustomRoute.replace(/^\//, ''),
                            from: lpParam,
                          } as any,
                        });
                      } else {
                        router.navigate({
                          to: activeConfig.noButtonCustomRoute,
                          search: { from: cleanPathname } as any,
                        });
                      }
                      return;
                    }

                    setSocialAnswer('No');
                    setHasInteracted(true);

                    const answerObj = {
                      question: questionTextToSave,
                      answer: 'No',
                    };
                    updateBasicInfo({ landingQuestion: answerObj });

                    if (isSocialDomain) {
                      const saveNoClaim = async () => {
                        try {
                          let claimNumber = loadFromStorage('currentId');
                          if (!claimNumber) {
                            const res = await generateSession({
                              utmTags: JSON.stringify(getUtmTagsFromUrl()),
                              allowNewClaim: true,
                            });
                            claimNumber = res.claimId;
                          }
                          updateClaim({
                            claimNumber: claimNumber,
                            landingQuestion: answerObj,
                          });
                        } catch (error) {
                          console.error('Failed to generate session for No answer:', error);
                        }
                      };
                      saveNoClaim();
                    }
                  }}
                  className="w-full py-4 px-6 rounded-xl text-center transition-all flex items-center justify-center bg-footer text-white hover:bg-neutral-800 active:scale-[0.98] font-bold text-body lg:text-title shadow-sm"
                >
                  {activeConfig.slug === '/black-horse' || lpParam === 'black-horse'
                    ? 'No, I had a different lender'
                    : 'No'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Header Group */}
      {(!homepageMode || !showSocialLayout || hasProceeded) && (
        <div className="w-full flex flex-col items-start gap-4">
          <h1 className="text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
            {activeConfig.slug === '/12-million' ? (
              <>
                Great! Let&rsquo;s <span className="text-primary">find your refund</span>.
              </>
            ) : (
              <>
                Let&rsquo;s see if you <span className="text-primary">could be owed</span> a refund.
              </>
            )}
          </h1>
          <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
            Pop your postcode to see if you could be owed{' '}
            <span className="text-primary font-bold text-subtitle">&pound;1,658*</span>
          </div>
        </div>
      )}

      {/* Input & Trust Group - tight gap */}
      <div className="w-full flex flex-col items-start gap-4">
        {(!homepageMode || !showSocialLayout || hasProceeded) && postCodeElementComp}

        <div
          style={{
            alignItems: 'end',
          }}
          className="flex flex-wrap justify-center gap-x-2 lg:gap-x-5 text-[clamp(10px,3.2vw,16px)] font-bold text-text-main w-full whitespace-nowrap"
        >
          <div className="flex items-center gap-1">
            <span>🔒</span>
            <span>Soft credit check only</span>
          </div>
          <span className="text-gray-300 font-light">&bull;</span>
          <div className="flex pt-1 items-center gap-1.5">
            <span>⏱️</span>
            <span>Takes just a few moments</span>
          </div>
          <span className="text-gray-300 font-light">&bull;</span>
          <div className="flex pt-1 items-center gap-1.5">
            <ShieldCheck size={16} className="text-primary shrink-0" strokeWidth={2.5} />
            <span>Automatic agreement search</span>
          </div>
        </div>

        {activeConfig.captionText && (
          <div className="w-full text-center text-micro text-text-muted font-bold -mt-2 leading-normal px-2 break-words">
            {activeConfig.captionText}
          </div>
        )}

        {homepageMode && (!showSocialLayout || hasProceeded || hasInteracted) && (
          <TrustpilotCarousel />
        )}

        {homepageMode && (
          <div className="w-full flex flex-col items-start gap-4 pt-6 border-t border-dashed border-primary/30">
            <div className="flex items-center gap-3.5">
              {/* <img
                src={imgSignature}
                alt="Signature"
                width={40}
                height={40}
                className="w-10 h-10 flex-shrink-0"
              /> */}
              <div className="flex flex-col leading-tight">
                <span className="text-text-main font-bold text-body tracking-tight">
                  Scott Mandeville
                </span>
                <span className="text-text-muted text-body font-medium mt-1">
                  Customer Care Specialist
                </span>
                {/* <span className="text-primary font-bold text-micro mt-0.5 uppercase tracking-wide">
                  FCA FRN 830181
                </span> */}
              </div>
            </div>

            <LcpDisclaimer />
          </div>
        )}

        {!homepageMode && (
          <div className="text-page-desc px-5">
            Check your eligibility today — simple, and clear
          </div>
        )}
      </div>
    </>
  );

  const getProgress = () => {
    if (homepageMode) return 0;
    return 20;
  };

  return (
    <MainFormContainer
      progress={getProgress()}
      hideAfterForm={hideAfterForm || (isSocialDomain && !hasInteracted)}
      currentStep={homepageMode ? undefined : 1}
      totalSteps={homepageMode ? undefined : 5}
      isHome={homepageMode}
      contentClassName={`p-5 lg:pb-20 flex flex-col items-start gap-6 ${
        homepageMode ? 'lg:px-0' : 'lg:px-20'
      }`}
    >
      {compIternal}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Important Notice">
        <p className="mb-4 text-[1.1rem]">
          Please ensure you have entered your <strong>current postcode</strong>.
        </p>
        <p className="mb-6 text-[1.1rem]">
          Do not provide previous addresses or previous names. Incorrect information may delay your
          verification.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => {
              updateClaim({ clickedChangePostcode: true });
              setIsModalOpen(false);
              postcodeRef.current?.setSubmitting?.(false);
            }}
            className="flex-1 py-3 px-4 border-2 border-primary text-primary rounded-xl font-bold hover:bg-primary/5 transition-colors"
          >
            Change Postcode
          </button>
          <button
            onClick={() => {
              updateClaim({ clickedProceed: true });
              handleContinue();
            }}
            className="flex-1 py-3 px-4 bg-primary text-white rounded-xl font-bold hover:bg-[#14532d] transition-colors"
          >
            Proceed
          </button>
        </div>
      </Modal>
    </MainFormContainer>
  );
}
